import { courseProgress } from "./course-progress";
import { accessEnded } from "./course-date";

/******************************************************************
 * ##: pickContinueCourse
 * Chooses the single course the "Continuar a aprender" hero action
 * should resume. Only courses the student can still open are ever
 * considered (an ended-access course is never targeted). Among those
 * it prefers, in order:
 *   1. Started + incomplete, most recent lesson activity first
 *      (Option B: "continue where you left off").
 *   2. Any incomplete course (e.g. not opened yet).
 *   3. If everything openable is complete, the first one (review).
 * Returns target = null when there is no openable course, so the
 * caller can hide the action (e.g. a single course with ended access).
 * @param {Array<object>} courses - course records from /users/courses
 * @returns {{ target: (object|null), isReview: boolean }} - chosen course + review flag
 * History:
 * 05-07-2026: Created
 ******************************************************************/
export const pickContinueCourse = (courses) => {
  const list = Array.isArray(courses) ? courses : [];

  // Only courses the student can still open (access not ended)
  const accessible = list.filter((course) => !accessEnded(course?.courses_students?.ended_at));
  if (accessible.length === 0) return { target: null, isReview: false };

  const isIncomplete = (course) => courseProgress(course) < 100;

  // Tier 1: started + incomplete, most recently active first (Option B)
  const resumable = accessible
    .filter((course) => course?.courses_students?.started === true && isIncomplete(course))
    .sort((a, b) => lastActivityAt(b) - lastActivityAt(a));

  // Tier 2: any incomplete openable course (e.g. never opened yet)
  const startable = accessible.filter(isIncomplete);

  // Tier 3: everything openable is complete -> review the first one
  const target = resumable[0] || startable[0] || accessible[0];
  const isReview = courseProgress(target) >= 100;

  return { target, isReview };
};

// HELPERS

/******************************************************************
 * ##: lastActivityAt
 * Most recent lesson completion time for a course, as a millisecond
 * timestamp. Used to rank started courses by recent activity. Returns
 * 0 when the course has no completed lesson (no usable timestamp).
 * @param {Object} course - one course record (with courses_students)
 * @returns {Number} - max completed_at in ms, or 0
 * History:
 * 05-07-2026: Created
 ******************************************************************/
const lastActivityAt = (course) => {
  const stamps = (course?.courses_students?.lessonStatus || [])
    .filter((lesson) => lesson.completed === true && lesson.completed_at)
    .map((lesson) => new Date(lesson.completed_at).getTime())
    .filter((time) => !Number.isNaN(time));

  return stamps.length ? Math.max(...stamps) : 0;
};

export default pickContinueCourse;
